"use client";

import React from "react";
import { Loader, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function GetInTouch() {
  return <section data-section-id="22241" id="start-your-project-form" className="bg-background text-foreground py-32 md:py-56 px-6 md:px-12 w-full overflow-hidden">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {}
          <div className="lg:col-span-5 space-y-8">
            
            <h2 className="font-sans text-6xl md:text-8xl font-bold leading-[0.9] tracking-tighter uppercase">
              Start <br />
              Your <br />
              Project
            </h2>
            <p className="font-mono text-sm md:text-base text-muted-foreground max-w-sm leading-relaxed">
              We help brands build strong online identities and grow their audience through technical craft and visual excellence.
            </p>
          </div>

          {}
          <div className="lg:col-span-7">
            <FormProvider formId="start_project_form_start_your_project" sectionName="get-in-touch" className="w-full" defaultValues={{
            name: "",
            email: "",
            company_type: "",
            phone: "",
            project_details: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            company_type: {
              required: false
            },
            phone: {
              required: false,
              pattern: "phone"
            },
            project_details: {
              required: true
            }
          }} submitText="Initiate Project" successMessage="Thank you. Our team will reach out to you shortly." errorMessage="Something went wrong. Please try again.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-12 border border-primary/10 bg-primary/5 backdrop-blur-sm text-center animate-in fade-in zoom-in duration-700 rounded-sm">
        <div className="mb-6 flex justify-center">
          <div className="size-12 rounded-sm border border-primary/20 flex items-center justify-center">
            <ArrowRight className="size-6 text-primary" />
          </div>
        </div>
        <h3 className="font-sans text-2xl font-bold mb-4 uppercase tracking-tight">Request Received</h3>
        <p className="font-mono text-xs text-muted-foreground uppercase tracking-[0.2em] leading-loose">
          {successMessage}
        </p>
      </div>;
  }
  const inputClasses = "border-0 border-b border-primary/20 rounded-none px-0 py-4 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary transition-all duration-500 placeholder:text-primary/20 h-auto text-lg md:text-xl font-mono";
  const labelClasses = "font-mono text-[10px] uppercase tracking-[0.2em] text-primary/40 mb-1 block";
  return <Form onSubmit={handleSubmit} className="space-y-10 md:space-y-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
        
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Type your name" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="hello@domain.com" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        <FormField control={control} name="company_type" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Industry Type</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className={`${inputClasses} w-full justify-between border-0 border-b border-primary/20 rounded-none shadow-none`}>
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="rounded-sm border border-primary/10 bg-background shadow-2xl">
                  {["Corporate", "Athlete / Influencer", "Creative Agency", "Startup", "Other"].map((option, i) => <SelectItem key={option} value={option} data-index={i} className="font-mono text-[11px] uppercase tracking-widest focus:bg-primary/5 focus:text-primary cursor-pointer py-3">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="space-y-0">
              <FormLabel className={labelClasses}>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (000) 000-0000" {...field} className={inputClasses} />
              </FormControl>
              <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
            </FormItem>} />
      </div>

      <FormField control={control} name="project_details" render={({
      field
    }) => <FormItem className="space-y-0">
            <FormLabel className={labelClasses}>Project Vision</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your brand goals and timeline" {...field} className={`${inputClasses} min-h-[100px] resize-none`} />
            </FormControl>
            <FormMessage className="mt-2 font-mono text-[10px] uppercase text-destructive" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/5 border border-destructive/10 text-destructive font-mono text-[10px] uppercase tracking-widest rounded-sm">
          {submitError}
        </div>}

      <div className="pt-6">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto h-14 px-8 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-sans font-bold text-base tracking-widest uppercase transition-all duration-500 shadow-lg">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Processing
            </> : <>
              Initiate Project
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
      </div>
    </Form>;
}